import { TARGET_FPS } from '@config/constants.js';

/**
 * AnimationLoop - Manages the render loop and frame updates
 *
 * Responsibilities:
 * - Handle requestAnimationFrame loop
 * - Update controls and scene
 * - Render scene via SceneManager
 * - Track performance metrics (FPS)
 * - Provide start/stop controls
 */
export class AnimationLoop {
  constructor(sceneManager, controls = null) {
    if (!sceneManager) {
      throw new Error('AnimationLoop requires a SceneManager instance');
    }

    this.sceneManager = sceneManager;
    this.controls = controls;
    this.isRunning = false;
    this.animationFrameId = null;

    // Performance tracking
    this.fps = 0;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.fpsUpdateInterval = 1000; // Update FPS every second

    // Callbacks for custom update logic
    this.updateCallbacks = [];
  }

  /**
   * Start the animation loop
   */
  start() {
    if (this.isRunning) {
      console.warn('AnimationLoop is already running');
      return;
    }

    this.isRunning = true;
    this.lastTime = performance.now();
    this._animate();
  }

  /**
   * Stop the animation loop
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Main animation loop function
   * @private
   */
  _animate() {
    if (!this.isRunning) {
      return;
    }

    this.animationFrameId = requestAnimationFrame(() => this._animate());

    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;

    // Update FPS counter
    this._updateFPS(currentTime);

    // Update controls if available (e.g., OrbitControls)
    if (this.controls && this.controls.update) {
      this.controls.update();
    }

    // Execute custom update callbacks
    this._executeUpdateCallbacks(deltaTime);

    // Render the scene
    this.sceneManager.render();
  }

  /**
   * Update FPS calculation
   * @param {number} currentTime - Current timestamp
   * @private
   */
  _updateFPS(currentTime) {
    this.frameCount++;

    const timeSinceLastUpdate = currentTime - this.lastTime;

    if (timeSinceLastUpdate >= this.fpsUpdateInterval) {
      this.fps = Math.round((this.frameCount * 1000) / timeSinceLastUpdate);
      this.frameCount = 0;
      this.lastTime = currentTime;
    }
  }

  /**
   * Execute all registered update callbacks
   * @param {number} deltaTime - Time since last frame in milliseconds
   * @private
   */
  _executeUpdateCallbacks(deltaTime) {
    for (const callback of this.updateCallbacks) {
      try {
        callback(deltaTime);
      } catch (error) {
        console.error('Error in update callback:', error);
      }
    }
  }

  /**
   * Register a callback to be executed on each frame
   * @param {Function} callback - Function to call each frame (receives deltaTime)
   */
  addUpdateCallback(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Update callback must be a function');
    }

    this.updateCallbacks.push(callback);
  }

  /**
   * Remove a previously registered callback
   * @param {Function} callback - The callback to remove
   */
  removeUpdateCallback(callback) {
    const index = this.updateCallbacks.indexOf(callback);
    if (index !== -1) {
      this.updateCallbacks.splice(index, 1);
    }
  }

  /**
   * Set the controls to be updated each frame
   * @param {Object} controls - Controls object (e.g., OrbitControls)
   */
  setControls(controls) {
    this.controls = controls;
  }

  /**
   * Get current FPS
   * @returns {number} Current frames per second
   */
  getFPS() {
    return this.fps;
  }

  /**
   * Check if the loop is running
   * @returns {boolean}
   */
  getIsRunning() {
    return this.isRunning;
  }

  /**
   * Reset the animation loop
   */
  reset() {
    this.stop();
    this.fps = 0;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.updateCallbacks = [];
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stop();
    this.updateCallbacks = [];
    this.controls = null;
    this.sceneManager = null;
  }
}
